/**
 * picking the right content template off a request folder.
 */

/**
 * The minimum a content template has to look like to be classified. Kept
 * structural so the casemedia and trash types — which model the same server
 * records under different names — can both be passed in.
 */
export interface ClassifiableContentTemplate {
  readonly sdo?: {
    readonly data?: {
      readonly caseName?: unknown;
      readonly folderTreeObjectId?: unknown;
      readonly projects?: unknown;
    };
  } | null;
}

/**
 * Does this template hold the request's projects?
 */
export const holdsProjects = (
  template: ClassifiableContentTemplate | null | undefined
): boolean => Array.isArray(template?.sdo?.data?.projects);

/**
 * Does this template look like the Redact Case record?
 */
export const looksLikeCaseTemplate = (
  template: ClassifiableContentTemplate | null | undefined
): boolean => {
  const data = template?.sdo?.data;
  return (
    !!data &&
    (typeof data.caseName === 'string' ||
      typeof data.folderTreeObjectId === 'string')
  );
};

/**
 * A template that holds projects and is NOT the case record — i.e. the
 * dedicated projects template of the final layout. This is the only thing a
 * caller looking for "some other record" should skip.
 */
export const isProjectsOnlyTemplate = (
  template: ClassifiableContentTemplate | null | undefined
): boolean => holdsProjects(template) && !looksLikeCaseTemplate(template);

/**
 * The folder's Redact Case template, or undefined.
 */
export const pickCaseContentTemplate = <T extends ClassifiableContentTemplate>(
  contentTemplates: readonly T[] | null | undefined
): T | undefined =>
  contentTemplates?.find(looksLikeCaseTemplate) ??
  contentTemplates?.find((t) => !isProjectsOnlyTemplate(t));

/**
 * The template holding the request's projects, under either layout, or
 * undefined when the request has none.
 */
export const pickProjectsContentTemplate = <
  T extends ClassifiableContentTemplate,
>(
  contentTemplates: readonly T[] | null | undefined
): T | undefined => contentTemplates?.find(holdsProjects);
